import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Undo2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormError, PageHeader } from "@/components/common/ui-bits";
import { api, errMsg } from "@/lib/api";
import { DOWNLOAD_ERROR } from "@/lib/download";

const FILE_ERROR = "Upload an .xlsx or .csv file under 5 MB (max 5,000 rows)";
const STATUS = { ready: "text-emerald-700", error: "text-rose-700", duplicate: "text-amber-700" };
const when = new Intl.DateTimeFormat("en-US", { timeZone: "America/Nassau", dateStyle: "medium", timeStyle: "short" });

async function downloadTemplate(kind) {
  try {
    const res = await api.get(`/imports/template/${kind}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = `DaLilShop_${kind[0].toUpperCase()}${kind.slice(1)}_Template.xlsx`;
    a.click();
  } catch { toast.error(DOWNLOAD_ERROR); }
}

function History({ refresh }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/imports").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load, refresh]);
  const undo = async (b) => {
    try { await api.post(`/imports/${b.id}/undo`); toast.success("Import undone"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="surface mt-8 overflow-hidden" data-testid="import-history">
      <h2 className="font-heading text-lg font-semibold px-5 pt-4 pb-3">Import history</h2>
      {rows.length === 0 ? <p className="px-5 pb-5 text-sm text-muted-foreground">No imports yet</p> : (
        <ul className="divide-y divide-border">
          {rows.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-sm" data-testid={`import-history-row-${b.id}`}>
              <span className="min-w-0 flex-1 basis-56"><b className="capitalize">{b.kind}</b> · <span className="break-all">{b.filename}</span>
                <span className="block text-[11px] text-muted-foreground">{when.format(new Date(b.created_at))} · {b.created_by_name}</span></span>
              <span className="text-xs">Imported {b.imported} · Skipped {b.skipped}</span>
              {b.undone_at ? <span className="text-xs font-semibold text-muted-foreground" data-testid={`import-undone-${b.id}`}>Undone</span> : (
                <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => undo(b)} data-testid={`import-undo-${b.id}`}><Undo2 className="h-3.5 w-3.5 mr-1" />Undo import</Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function ImportPage() {
  const [kind, setKind] = useState("transactions");
  const [preview, setPreview] = useState(null);
  const [dups, setDups] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [refresh, setRefresh] = useState(0);
  const fileRef = useRef(null);

  const choose = (k) => { setKind(k); setPreview(null); setError(""); setResult(""); };
  const upload = async (file) => {
    if (!file) return;
    setError(""); setResult(""); setPreview(null);
    if (!/\.(xlsx|csv)$/i.test(file.name) || file.size > 5 * 1024 * 1024) return setError(FILE_ERROR);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    setBusy(true);
    try { setPreview((await api.post("/imports/preview", fd)).data); } catch (e) { setError(errMsg(e)); }
    setBusy(false);
    fileRef.current.value = "";
  };
  const n = preview ? preview.counts.ready + (dups ? preview.counts.duplicate : 0) : 0;
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${preview.preview_id}/commit`, { include_duplicates: dups });
      setResult(data.message); setPreview(null); setRefresh((x) => x + 1); toast.success(data.message);
    } catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };
  const cols = preview ? Object.keys(preview.rows.find((r) => r.status !== "error")?.display || preview.rows[0]?.display || {}) : [];

  return (
    <div className="max-w-5xl">
      <PageHeader eyebrow="Bulk upload from Excel or CSV" title="Import" testId="import-title" />
      <div className="grid grid-cols-2 gap-1.5 rounded-2xl bg-secondary p-1.5 max-w-md mb-5">
        {["transactions", "bills"].map((k) => (
          <button key={k} onClick={() => choose(k)} data-testid={`import-kind-${k}`} aria-pressed={kind === k}
            className={`rounded-xl py-2.5 text-sm font-semibold capitalize ${kind === k ? "bg-card shadow-sm" : "text-foreground/70"}`}>{k}</button>
        ))}
      </div>
      <div className="surface p-5 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Button variant="outline" className="rounded-xl h-11" onClick={() => downloadTemplate(kind)} data-testid="import-download-template"><Download className="h-4 w-4 mr-1.5" />Download template</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="import-file-input" />
        <Button className="rounded-xl h-11" disabled={busy} onClick={() => fileRef.current.click()} data-testid="import-upload-button">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Upload .xlsx or .csv</Button>
        <span className="text-xs text-muted-foreground">Max 5 MB, 5,000 rows. Dates like 2026-10-05, 10/05/2026 or 05-Oct-2026.</span>
      </div>
      <div className="mt-4 space-y-3"><FormError message={error} testId="import-error" />
        {result && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-900" data-testid="import-result">{result}</div>}</div>
      {preview && (
        <section className="surface mt-5 overflow-hidden" data-testid="import-preview">
          <div className="px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border text-sm">
            <b className="break-all">{preview.filename}</b>
            <span className="text-emerald-700" data-testid="import-count-ready">{preview.counts.ready} Ready</span>
            <span className="text-rose-700" data-testid="import-count-error">{preview.counts.error} Errors</span>
            <span className="text-amber-700" data-testid="import-count-duplicate">{preview.counts.duplicate} Possible duplicates</span>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={dups} onCheckedChange={(v) => setDups(!!v)} data-testid="import-include-duplicates" />Include duplicates</label>
            <Button className="rounded-xl h-10 sm:ml-auto w-full sm:w-auto" disabled={!n || busy} onClick={commit} data-testid="import-commit-button">Import {n} rows</Button>
          </div>
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-foreground text-white text-[11px] uppercase tracking-wider sticky top-0">
                <tr><th className="px-3 py-2 text-left">Row</th><th className="px-3 py-2 text-left">Result</th>{cols.map((c) => <th key={c} className="px-3 py-2 text-left">{c}</th>)}</tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} className="border-b border-border" data-testid={`import-row-${r.row}`}>
                    <td className="px-3 py-2 text-muted-foreground">{r.row}</td>
                    <td className={`px-3 py-2 font-semibold ${STATUS[r.status]}`} data-testid={`import-row-status-${r.row}`}>{r.message}</td>
                    {cols.map((c) => <td key={c} className="px-3 py-2 whitespace-nowrap">{r.display?.[c] ?? ""}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <History refresh={refresh} />
    </div>
  );
}
